import React from 'react';
import ReactDOMServer from 'react-dom/server';

import spreadsheetExampleImg from '../../assets/images/dob_spreadsheet_example.png';
import groceriesChannelAImg from '../../assets/images/help_groceriesChannel_a.png';
import groceriesChannelBImg from '../../assets/images/help_groceriesChannel_b.png';

export default {
    admin: {
        title: "Admin",
        offices: {
            title: "Offices",
            cols: {
                location: "Location"
            }
        },
        users: {
            title: "Users",
            cols: {
                email: "Email",
                fullName: "Full Name",
                role: "Region/Role"
            }
        }
    },
    history: {
        title: "History",
        cols: {
            office: "Office",
            action: "Action",
            modifiedBy: "Modified By",
            date: "Date",
            user: "User",
            event: "Event"
        }
    },
    birthdays: {
        add: "Add Birthday",
        delete: "Remove Birthday",
        deleteMulti: "Remove Birthdays",
        edit: "Edit Birthday",
        spreadsheetHelp: "Click to open the help documentation.",
        title: "Birthdays",
        cols: {
            customization: "Customization",
            date: "Date of Birth",
            name: "Name"
        },
        fields: {
            date: "Date of Birth",
            description: "Description",
            image: "Image",
            name: "Name",
            sheetUrl: "Google spreadsheet URL",
            title: "Title"
        },
        help: {
            spreadsheetUpload: {
                brief: 'Click to open the help documentation.',
                content: ReactDOMServer.renderToStaticMarkup(
                    <div>
                        <h2>1. Create a spreadsheet</h2>
                        <p>Create a spreadsheet using Google Docs, Excel, or your program of choice.</p>
                        <p>Structure it so that the names appear in the second column, and dates of birth in the third.</p>
                        <p>Use the first row for headers, or leave them blank.</p>
                        <p>Feel free to use the first column however you like; it will not be used.</p>
                        <p><em>The following is an example of the desired layout:</em></p>
                        <p><img src={spreadsheetExampleImg} alt="spreadsheet example"/></p>

                        <h2>2. Upload the spreadsheet</h2>
                        <p><em>** If you've created your spreadsheet on Google Docs, skip to the next step.</em></p>
                        <p>Now that you have your spreadsheet, log into <a href="https://drive.google.com/">Google Drive</a>, and
                            upload the file to your drive.</p>
                        <p>If you do not have a Google account and do not wish to create one, feel free to contact Chase Thorne on
                            MatterMost, and he'll gladly upload the file for you.</p>

                        <h2>3. Load the data</h2>
                        <p>Open the spreadsheet in Google Docs.</p>
                        <p>Click on the blue "Share" button on the top right corner, and ensure you have the permissions set to
                            "Anyone with the link can view".</p>
                        <p>Copy the document's URL from the address bar, and paste it into the text field.</p>
                        <p>After submitting, the process may take a few moments to load the data.</p>
                    </div>
                )
            }
        }
    },
    buttons: {
        add: "Add",
        archive: "Archive",
        cancel: "Cancel",
        close: "Close",
        confirm: "Confirm",
        delete: "Remove",
        edit: "Edit",
        pickColor: "Pick Color",
        restore: "Restore",
        submit: "Submit",
        submitting: "Submitting..."
    },
    common: {
        actions: "Actions",
        archiveConfirm: "Confirm Archival",
        archiveMultiConfirm: "Are you sure you want to archive the selected items?",
        confirmMessage: "Are you sure you want to ",
        deleteConfirm: "Confirm Removal",
        deleteMultiConfirm: "Are you sure you want to remove the selected items?",
        unarchiveMultiConfirm: "Are you sure you want to unarchive the selected items?"
    },
    config: {
        basicSettings: "Basic Settings",
        birthdaySettings: "Default Birthday Settings",
        defaultImageSettings: "Default Image Settings",
        eventSlideFontSettings: "Event Slide Font Settings",
        grocerySettings: "Grocery Settings",
        polishSettings: "Polish Settings",
        roomSettings: "Room Settings",
        updateFailure: "Failed to save changes.",
        updateSuccess: "Configuration updated.",
        tinymceLanguage:"en_US",
        fields: {
            common: {
                description: "Description",
                details: "Details",
                title: "Title"
            },
            autoReload: "Automatically reload dashboard on update",
            birthdays: {
                dateOfBirthFontSettings: "Default Date of Birth Font Settings",
                displayDeltaAfter: "Display-after delta",
                displayDeltaBefore: "Display-before delta",
                image: "Image",
            },
            spoc: {
                defaultSpocImage: "Default SPOC Image (Max Image Size: 688px X 430px)",
            },
            events: {
                eventUsers: "Outlook event users",
            },
            defaultDuration: "Default slide duration",
            groceriesChannel: "Mattermost Groceries Channel",
            groceriesFontSettings: "List item font settings",
            modules: "Modules",
            polish: {
                sheetId: "Polish Spreadsheet Id",
                tabName: "Polish Spreadsheet Tab Name"
            },
            previewOrientation: "Default preview orientation",
            rooms: "Conference Rooms",
            timezone: "Timezone",
            weatherCityId: "City Id (weather)"
        },
        help: {
            birthdays: {
                brief: 'Use $NAME to substitute for a name.',
                detailed: ReactDOMServer.renderToStaticMarkup(
                    <div>
                        <h3>Write a generic message</h3>
                        <p>Use "$NAME" in place of a person's name.</p>
                        <pre>"Happy birthday, $NAME!"</pre>

                        <h3>Automatically substitutes in the name</h3>
                        <pre>$NAME = "Fred"</pre>
                        <pre>displays -> "Happy birthday, Fred!"</pre>
                    </div>
                )
            },
            birthdaysDeltaAfter: {
                brief: "The number of days after the birthday you wish the slide to be visible for. \nDefaults to one day."
            },
            birthdaysDeltaBefore: {
                brief: "The number of days before the birthday you wish the slide to be visible for. \nDefaults to one day."
            },
            eventUsers: {
                brief: 'Enter comma separated usernames.',
                detailed: ReactDOMServer.renderToStaticMarkup(
                    <div>
                        <h3>Find the person's company email address.</h3>
                        <pre>cthorne@spiria.com</pre>

                        <h3>Strip the "@spiria.com" from the email.</h3>
                        <pre>cthorne</pre>

                        <h3>(Optional) Add another user with a comma separator</h3>
                        <pre>cthorne, lmeleshchenko</pre>
                    </div>
                )
            },
            groceriesChannel: {
                brief: 'Enter MatterMost channel name.',
                detailed: ReactDOMServer.renderToStaticMarkup(
                    <div>
                        <h3>Open channel info</h3>
                        <p>Open MatterMost and enter the channel you wish to use. Click on the title and select "View Info" from the menu.</p>
                        <img src={groceriesChannelAImg} alt="open channel info help"/>

                        <h3>Get the channel name</h3>
                        <p>Copy the channel name from the URL field.</p>
                        <img src={groceriesChannelBImg} alt="copy channel name help"/>
                    </div>
                )
            },
            timezone: {
                brief: 'Enter your timezone.',
                detailed: ReactDOMServer.renderToStaticMarkup(
                    <div>
                        <h3>Hint</h3>
                        <p>Start typing in your country/continent, and find your region in the options.</p>
                        <pre>eg. "Can" for Canadian timezones</pre>

                        <h3>Find your timezone</h3>
                        <p>Visit the <a href="https://momentjs.com/timezone/">Moment Timezone</a> homepage.</p>
                        <p>Scroll down to the map, and mouse over the dot closest to your location.</p>
                        <p>Take note of the text above the map; this is your timezone.</p>

                        <h3>Enter the timezone</h3>
                        <p>Return to the form.</p>
                        <p>Start typing in the timezone, and it will attempt to auto-complete.</p>
                    </div>
                )
            },
            weatherCityId: {
                brief: 'Type in the full name of your city, and select the best option.'
            }
        }
    },
    countdown: {
        days: "days",
        hours: "hours",
        minutes: "minutes",
        seconds: "seconds"
    },
    error: {
        report: {
            button: "Report feedback",
            message: "Something has gone horribly wrong and caused the application to close.\n"
            + "Please take the time to submit feedback, and refresh the page when you are ready to continue.",
            title: "An error has occurred!"
        }
    },
    events: {
        add: "Add Event",
        archive: "Archive Event",
        archiveMulti: "Archive Events",
        delete: "Remove Event",
        deleteMulti: "Remove Events",
        edit: "Edit Event",
        restore: "Restore Event",
        title: "Events",
        unarchiveMulti: "Unarchive Events",
        cols: {
            createdBy: "Author",
            description: "Description",
            endDate: "End",
            order: "Order",
            title: "Title",
            startDate: "Start",
            visibility: "Visibility"
        },
        fields: {
            backgroundColor: "Background Color",
            calendarEvent: "Calendar Event",
            contact: "Contact Info",
            description: "Description",
            duration: "Slide Duration",
            urlLink:"SPOC URL link",
            endDate: "End Date",
            imageDefault: "Image (Suggested Image Ratio: 3:2)",
            imageImage: "Image (Suggested Image Ratio: 9:16)",
            isArchived: "Archived",
            isCountdown: "Count down to time",
            location: "Location",
            participants: "Participants",
            startDate: "Start Date",
            time: "Time",
            timeOverride: "Enter time manually",
            title: "Title",
            type: "Slide Type",
            spocLayout: "SPOC Layout",
            spocImage: "SPOC Image (Suggested Image Size: 413px X 430px)",
            spocFullImage: "SPOC Image (Suggested Image Size: 688px X 430px)",
            hideTitleOnSpoc: 'Hide title on SPOC'
        }
    },
    eventPreview: {
        button: "Preview",
        rotate: "Rotate",
        title: "Event Preview",
        spoc: "Spoc",
    },
    forms: {
        fileDrop: {
            dropHere: "Drag and drop a file here to upload it."
        },
        fonts: {
            lineHeight: "Line Height",
            size: "Font Size",
            units: "Units",
            weight: "Font Weight"
        },
        imageSettings: "Dashboard - Advanced Image Settings",
        imageSettingsFields: {
            objectFit: "Object Fit",
            horizontalPosition: "Horizontal Position",
            verticalPosition: "Vertical Position"
        },
        spocImageSettings: "SPOC - Advanced Image Settings",
        spocImageSettingsFields: {
            objectFit: "Object Fit",
            horizontalPosition: "Horizontal Position",
            verticalPosition: "Vertical Position"
        },
        optional: "Optional Fields",
        required: "Required Fields"
    },
    groceries: {
        add: "Add Groceries",
        delete: "Remove Groceries",
        deleteMulti: "Remove Groceries",
        edit: "Edit Groceries",
        title: "Groceries",
        cols: {
            name: "Name",
            requestedBy: "Requested By"
        },
        fields: {
            name: "Grocery Item",
            requestedBy: "Requested By"
        },
        slides: {
            title: "All you can eat!"
        }
    },
    help :{
        seeLess: "Click here to see less",
        seeMore: "Click here to see more",
        title: "Help"
    },
    login: {
        dashboard: "Dashboard",
        email: "Email",
        forgotPassword: "Forgot Password?",
        password: "Password",
        return: "Return to Dashboard",
        signIn: "Sign In",
        errors: {
            "auth/wrong-password": "The password is invalid or the user does not have a password.",
            "auth/user-not-found": "There is no user record corresponding to this identifier. The user may have been deleted."
        }
    },
    messages: {
        cols: {
            datetime: "Date",
            text: "Message",
            username: "Username"
        }
    },
    navigation: {
        admin: "ADMIN",
        all: "ALL",
        birthdays: "Birthdays",
        config: "Config",
        events: "Events",
        groceries: "Groceries",
        messages: "Messages",
        offices: "Offices",
        users: "Users",
        videos: "Videos",
        systemAnnouncements: "System Announcements"
    },
    offices: {
        add: "Add Office",
        archive: "Archive Office",
        delete: "Remove Office",
        cols: {
            key: "Location"
        },
        fields: {
            key: "Location"
        }
    },
    systemAnnouncements: {
        add: "Add System Announcement",
        edit: "Edit System Announcement",
        delete: "Delete Announcement",
        cols: {
            title: "Title",
            announcement: "Announcement",
            startDate: "Start Date",
            endDate: "End Date"
        },
        fields: {
            title: "Title",
            announcement: "Announcement",
            startDate: "Announcement Start Date",
            endDate: "Announcement End Date"
        },
    },
    profile: {
        edit: "Edit Profile",
        logout: "Log Out",
        resetPassword: "Reset Password",
        passwordResetMessage: "Are you sure you want to reset your password?",
        passwordResetSuccess: "You should receive a password reset email shortly."
    },
    rooms: {
        available: "Available",
        forTime: "for {time}"
    },
    splash: {
        select: "Select a Dashboard:",
        goToAdmin: "Go to Admin Panel"
    },
    tables: {
        archivedData: "Archived Data",
        noData: "No Data",
        scheduledAnnouncements: "Scheduled Announcements",
        pastAnnouncements: "Past Announcements"

    },
    types: {
        imageFit: {
            contain: "Contain",
            cover: "Cover",
            fill: "Fill (Default)"
        },
        imagePosition: {
            bottom: "Bottom",
            center: "Center (Default)",
            left: "Left",
            right: "Right",
            top: "Top"
        },
        module: {
            birthdays: "Birthdays",
            groceries: "Groceries",
            messages: "Messages",
            polish: "Polish",
            rooms: "Rooms",
            videos: "Videos",
            spoc: "SPOC"
        },
        orientation: {
            landscape: "Landscape",
            portrait: "Portrait"
        },
        role: {
            admin: "Admin",
            all: "All",
            superAdmin: "Super Admin"
        },
        slide: {
            default: "Default",
            image: "Image"
        },
        spocLayout: {
            titleOnTop: "Title on Top",
            fullImage: "Full Image",
            visualOnTop: "Visual on Top"
        }
    },
    users: {
        add: "Add User",
        delete: "Remove User",
        edit: "Edit User",
        cols: {
            email: "Email",
            fullName: "Name",
            role: "Region/Role(s)"
        },
        fields: {
            email: "Email",
            fullName: "Full Name",
            role: "Region/Role(s)"
        }
    },
    videos: {
        add: "Add Video",
        archive: "Archive Video",
        delete: "Remove Video",
        edit: "Edit Video",
        restore: "Restore Video",
        cols: {
            title: "Title",
            url: "URL"
        },
        fields: {
            title: "Title",
            url: "URL"
        }
    }
};


// WEBPACK FOOTER //
// ./src/constants/locale/en-CA.js